import { useEffect, useRef, type KeyboardEvent } from 'react';

/**
 * Shared focus-management contract for the top-up desk's dialogs (confirm,
 * reject, direct top-up) — the SAME two rules `RefundConfirmDialog`
 * (`deposit-row.tsx`) already applies to its two buttons, generalised to a
 * dialog with more than two focusable fields:
 *
 *  1. Tab never escapes the dialog — it wraps from the last focusable element
 *     back to the first (and Shift+Tab from the first to the last).
 *  2. Closing (Escape, cancel, backdrop click, or a settled mutation) returns
 *     focus to whatever had it before the dialog opened — the row's trigger
 *     button — rather than dropping it back to `<body>`.
 */

const FOCUSABLE_SELECTOR =
  'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/** Handle inside a dialog's `onKeyDown`: call on every keydown, it no-ops for anything but Tab. */
export function trapTabKey(container: HTMLElement | null, event: KeyboardEvent<HTMLDivElement>): void {
  if (event.key !== 'Tab' || !container) return;
  const focusable = Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR));
  if (focusable.length === 0) return;
  const first = focusable[0]!;
  const last = focusable[focusable.length - 1]!;
  if (event.shiftKey && document.activeElement === first) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && document.activeElement === last) {
    event.preventDefault();
    first.focus();
  }
}

/**
 * Remembers whatever element had focus right before this dialog mounted (the
 * row's trigger button, by construction — a click focuses it first) and
 * refocuses it the moment the dialog unmounts, however that happens.
 */
export function useReturnFocusOnUnmount(): void {
  const triggerRef = useRef<HTMLElement | null>(null);
  useEffect(() => {
    triggerRef.current = document.activeElement as HTMLElement | null;
    return () => {
      triggerRef.current?.focus();
    };
  }, []);
}
